import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { getSandboxesQueryOptions } from '@/api/getSandboxes';
import { useManageForm } from '@/components/form/useManageForm';
import { useGame } from '@/hooks/useGame';

import { useCreateSandbox } from '../api/createSandbox';
import {
  createSandboxSchema,
  type CreateSandbox,
} from '../models/createSandbox';

const defaultCreateSandbox = (): CreateSandbox => ({
  game_code: '',
  code: '',
  name: '',
  description: '',
  is_active: true,
});

export const useSandboxFormLogic = ({
  close,
}: {
  close: (close: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const { game_code: gameCode } = useGame();
  const { handleSubmit, isSubmitting, form } = useManageForm(
    useForm<CreateSandbox>({
      resolver: zodResolver(createSandboxSchema),
      defaultValues: {
        ...defaultCreateSandbox(),
        game_code: gameCode,
      },
    }),
  );

  const createSandboxMutation = useCreateSandbox({
    gameCode,
    mutationConfig: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getSandboxesQueryOptions({ game_code: gameCode }).queryKey,
        });
        close(true);
      },
    },
  });

  const submit = async (formData: CreateSandbox) => {
    await createSandboxMutation.mutateAsync({
      ...formData,
      name: formData.code,
    });
  };

  return {
    form,
    handleSubmit,
    isSubmitting: isSubmitting || createSandboxMutation.isPending,
    submit,
  };
};
